/**
 * Tracks premium wall/paywall events for conversion funnel
 * Correlation IDs persist in localStorage to survive page redirects (30 min expiry)
 */

import mixpanelService from '../mixpanel-service'
import bannerTracker from './banner-tracker'
import { generateUUID } from '@utils/uuid'
import {
    EVENTS,
    PROPS,
    STORAGE_KEYS,
    CORRELATION_ID_EXPIRY_MS
} from './analytics-spec'

class PaywallTracker {
    constructor() {
        if (PaywallTracker.instance) {
            return PaywallTracker.instance
        }
        PaywallTracker.instance = this

        this._paywallInstanceId = null
        this._purchaseAttemptId = null
        this._wallType = null
        this._productId = null
        this._billingPeriod = null
        this._isTrial = null
        this._isReturning = null

        this._restoreFromStorage()
    }

    _restoreFromStorage() {
        this._paywallInstanceId = this._getStoredId(STORAGE_KEYS.PAYWALL_INSTANCE)
        this._purchaseAttemptId = this._getStoredId(STORAGE_KEYS.PURCHASE_ATTEMPT)
        this._restorePurchasePlan()
    }

    _getStoredId(key) {
        try {
            const data = localStorage.getItem(key)
            if (!data) return null

            const parsed = JSON.parse(data)
            if (parsed.expiry > Date.now()) {
                return parsed.id
            }
            localStorage.removeItem(key)
        } catch (e) {
            // Ignore parse errors
        }
        return null
    }

    _persistId(key, id) {
        try {
            localStorage.setItem(key, JSON.stringify({
                id,
                expiry: Date.now() + CORRELATION_ID_EXPIRY_MS
            }))
        } catch (e) {
            // Ignore storage errors
        }
    }

    /**
     * Track when a premium wall is shown
     */
    onWallShown({ wallType, presentation, triggerReason, entryPoint }) {
        this._paywallInstanceId = generateUUID()
        this._wallType = wallType
        this._persistId(STORAGE_KEYS.PAYWALL_INSTANCE, this._paywallInstanceId)

        mixpanelService.track(EVENTS.PREMIUM_WALL_SHOWN, {
            [PROPS.PAYWALL_INSTANCE_ID]: this._paywallInstanceId,
            [PROPS.WALL_TYPE]: wallType,
            [PROPS.PRESENTATION]: presentation,
            [PROPS.TRIGGER_REASON]: triggerReason,
            [PROPS.ENTRY_POINT]: entryPoint
        })
    }

    /**
     * Track when user taps a CTA on the premium wall
     */
    onCtaTapped(cta) {
        mixpanelService.track(EVENTS.PREMIUM_WALL_CTA_TAPPED, {
            [PROPS.PAYWALL_INSTANCE_ID]: this._paywallInstanceId,
            [PROPS.CTA]: cta,
            [PROPS.WALL_TYPE]: this._wallType
        })
    }

    /**
     * Track when user dismisses the premium wall
     */
    onWallDismissed(dismissMethod) {
        mixpanelService.track(EVENTS.PREMIUM_WALL_DISMISSED, {
            [PROPS.PAYWALL_INSTANCE_ID]: this._paywallInstanceId,
            [PROPS.DISMISS_METHOD]: dismissMethod,
            [PROPS.WALL_TYPE]: this._wallType
        })
    }

    /**
     * Track when user starts a purchase - call before redirecting to payment.
     * Captures the plan breakdown and persists it so the post-redirect
     * purchase_succeeded (a fresh page load) can report it.
     */
    onPurchaseStarted({ productId, billingPeriod, isTrial, isReturning }) {
        this._purchaseAttemptId = generateUUID()
        this._productId = productId
        this._billingPeriod = billingPeriod
        this._isTrial = isTrial
        this._isReturning = isReturning
        this._persistId(STORAGE_KEYS.PURCHASE_ATTEMPT, this._purchaseAttemptId)
        this._persistPurchasePlan()

        try {
            localStorage.setItem(STORAGE_KEYS.PURCHASE_TIMESTAMP, Date.now().toString())
        } catch (e) {
            // Ignore storage errors
        }

        mixpanelService.track(EVENTS.PURCHASE_STARTED, {
            [PROPS.PAYWALL_INSTANCE_ID]: this._paywallInstanceId,
            [PROPS.PURCHASE_ATTEMPT_ID]: this._purchaseAttemptId,
            ...this._planProps(),
            ...bannerTracker.getPurchaseAttributionProps()
        })
    }

    /**
     * Track when payment succeeds - call when user returns from payment with
     * success state. Plan breakdown comes from the descriptor persisted at
     * checkout (restored on this fresh page load).
     */
    onPurchaseSucceeded() {
        mixpanelService.track(EVENTS.PURCHASE_SUCCEEDED, {
            [PROPS.PAYWALL_INSTANCE_ID]: this._paywallInstanceId,
            [PROPS.PURCHASE_ATTEMPT_ID]: this._purchaseAttemptId,
            ...this._planProps(),
            ...bannerTracker.getPurchaseAttributionProps()
        })
    }

    /**
     * Track when entitlement sync begins (after returning from payment)
     */
    onEntitlementSyncStarted(productId, transactionId) {
        const props = {
            [PROPS.PAYWALL_INSTANCE_ID]: this._paywallInstanceId,
            [PROPS.PURCHASE_ATTEMPT_ID]: this._purchaseAttemptId,
            [PROPS.PRODUCT_ID]: productId
        }

        if (transactionId) {
            props[PROPS.TRANSACTION_ID] = transactionId
        }

        mixpanelService.track(EVENTS.ENTITLEMENT_SYNC_STARTED, props)
    }

    /**
     * Track entitlement sync result
     */
    onEntitlementSyncResult(result, transactionId) {
        let timeSincePurchaseMs = 0
        try {
            const purchaseTimestamp = localStorage.getItem(STORAGE_KEYS.PURCHASE_TIMESTAMP)
            if (purchaseTimestamp) {
                timeSincePurchaseMs = Date.now() - parseInt(purchaseTimestamp, 10)
            }
        } catch (e) {
            // Ignore errors
        }

        const props = {
            [PROPS.PAYWALL_INSTANCE_ID]: this._paywallInstanceId,
            [PROPS.PURCHASE_ATTEMPT_ID]: this._purchaseAttemptId,
            [PROPS.RESULT]: result,
            [PROPS.TIME_SINCE_PURCHASE_MS]: timeSincePurchaseMs
        }

        if (transactionId) {
            props[PROPS.TRANSACTION_ID] = transactionId
        }

        mixpanelService.track(EVENTS.ENTITLEMENT_SYNC_RESULT, props)
        this._clearStorage()
    }

    _planProps() {
        return {
            [PROPS.PRODUCT_ID]: this._productId,
            [PROPS.BILLING_PERIOD]: this._billingPeriod,
            [PROPS.IS_TRIAL]: this._isTrial,
            [PROPS.IS_RETURNING]: this._isReturning
        }
    }

    _persistPurchasePlan() {
        try {
            localStorage.setItem(STORAGE_KEYS.PURCHASE_PLAN, JSON.stringify({
                productId: this._productId,
                billingPeriod: this._billingPeriod,
                isTrial: this._isTrial,
                isReturning: this._isReturning,
                expiry: Date.now() + CORRELATION_ID_EXPIRY_MS
            }))
        } catch (e) {
            // Ignore storage errors
        }
    }

    _restorePurchasePlan() {
        try {
            const data = localStorage.getItem(STORAGE_KEYS.PURCHASE_PLAN)
            if (!data) return
            const parsed = JSON.parse(data)
            if (parsed.expiry > Date.now()) {
                this._productId = parsed.productId
                this._billingPeriod = parsed.billingPeriod
                this._isTrial = parsed.isTrial
                this._isReturning = parsed.isReturning
            } else {
                localStorage.removeItem(STORAGE_KEYS.PURCHASE_PLAN)
            }
        } catch (e) {
            // Ignore parse errors
        }
    }

    _clearStorage() {
        try {
            localStorage.removeItem(STORAGE_KEYS.PAYWALL_INSTANCE)
            localStorage.removeItem(STORAGE_KEYS.PURCHASE_ATTEMPT)
            localStorage.removeItem(STORAGE_KEYS.PURCHASE_TIMESTAMP)
            localStorage.removeItem(STORAGE_KEYS.PURCHASE_PLAN)
            bannerTracker.clearAttribution()
        } catch (e) {
            // Ignore errors
        }
    }

    getPaywallInstanceId() {
        return this._paywallInstanceId
    }

    getPurchaseAttemptId() {
        return this._purchaseAttemptId
    }

    _resetForTesting() {
        this._paywallInstanceId = null
        this._purchaseAttemptId = null
        this._wallType = null
        this._productId = null
        this._billingPeriod = null
        this._isTrial = null
        this._isReturning = null
    }
}

export default new PaywallTracker()
